import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Upload } from "lucide-react";
import { BRAND_CONTEXT_CATEGORIES, BrandContextDocument } from "@/types/brand-context";
import { formatBytes, parseTags } from "./format";
import { CategorySelect } from "./CategorySelect";
import { FIELD_LABEL_CLASS, TagsInput } from "./TagsInput";

const ACCEPTED_MIME_EXTENSIONS = ".pdf,.txt,.md,.csv";

interface DocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingDocument?: BrandContextDocument | null;
  isPending?: boolean;
  onSubmitCreate: (input: { file: File; title: string; category: string; tags: string[] }) => void;
  onSubmitUpdate: (input: { title: string; category: string; tags: string[] }) => void;
}

export function DocumentDialog({
  open,
  onOpenChange,
  existingDocument,
  isPending = false,
  onSubmitCreate,
  onSubmitUpdate,
}: DocumentDialogProps) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<string>(BRAND_CONTEXT_CATEGORIES[0]);
  const [tagsText, setTagsText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(existingDocument?.title ?? "");
    setCategory(existingDocument?.category ?? BRAND_CONTEXT_CATEGORIES[0]);
    setTagsText(existingDocument?.tags.join(", ") ?? "");
    setFile(null);
  }, [open, existingDocument]);

  const isEditing = !!existingDocument;
  const canSubmit = title.trim() !== "" && (isEditing || file !== null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    if (selected && title.trim() === "") {
      setTitle(selected.name.replace(/\.[^.]+$/, ""));
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    const tags = parseTags(tagsText);

    if (isEditing) {
      onSubmitUpdate({ title: title.trim(), category, tags });
    } else if (file) {
      onSubmitCreate({ file, title: title.trim(), category, tags });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Entry" : "Upload Document"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update this document's title, category, and tags."
              : "Brand voice guides, style docs, research PDFs — anything Signal AI should reference for this brand."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!isEditing && (
            <div>
              <Label className={FIELD_LABEL_CLASS}>File</Label>
              <div className="mt-1.5">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_MIME_EXTENSIONS}
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full rounded-lg border border-dashed border-border bg-surface-tint-soft hover:bg-primary/5 hover:border-primary transition-colors px-4 py-6 text-center"
                >
                  <Upload className="h-5 w-5 mx-auto text-muted-foreground" />
                  <p className="text-sm text-foreground mt-2">
                    {file ? file.name : "Click to choose a file"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {file ? formatBytes(file.size) : "PDF, TXT, MD, CSV — max 10MB"}
                  </p>
                </button>
              </div>
            </div>
          )}
          <div>
            <Label className={FIELD_LABEL_CLASS}>Title</Label>
            <Input
              placeholder="Brand Voice & Tone Guidelines"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className={FIELD_LABEL_CLASS}>Category</Label>
            <CategorySelect value={category} onValueChange={setCategory} className="mt-1.5" />
          </div>
          <TagsInput value={tagsText} onChange={setTagsText} placeholder="voice, tone, guidelines" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isPending}>
            {isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEditing ? "Save Changes" : "Add to Brand Context"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
